import {FormControlLabel, IconButton, MenuItem, Paper, Radio, Switch} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import {Scope} from 'Shared/constants/Scope';
import {changeEnrollTrackRole, changeIsUserSupport, removeUserType} from 'Users/ducks/userEntry';
import {Application} from 'Users/models/Application';
import {
  EnrollTrackRole,
  InvalidEnrollmentTrackingUserType,
  ValidEnrollmentTrackingUserType,
} from 'Users/models/User/EnrollmentTrackingUserType';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './EnrollTrackUserTypeEntry.sass';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import {createSnackbar} from 'Core/ducks/snackbar';
import {useState} from 'react';

type EnrollTrackUserTypeEntryProps = {
  enrollTrackUserType: InvalidEnrollmentTrackingUserType | ValidEnrollmentTrackingUserType;
  isEdit: boolean;
  isExternal: boolean;
  isUserSupport: boolean;
  hasEnrollTrackAgentConnection: boolean;
};

export const rolesThatCanSupportUsers = [
  EnrollTrackRole.Admin,
  EnrollTrackRole.EtsLeadership,
  EnrollTrackRole.EtsEnrollmentOperations,
  EnrollTrackRole.EtsDataOperations,
  EnrollTrackRole.HomeOffice,
];

const EnrollTrackUserTypeEntry = (props: EnrollTrackUserTypeEntryProps) => {
  const dispatch = useDispatch();
  const {enrollTrackUserType, isExternal, hasEnrollTrackAgentConnection, isUserSupport} = props;
  const {enrollTrackRole} = enrollTrackUserType.value;

  const scopes = useSelector(selectUserScopes);
  const hasUpdateAccess = hasAccessToScopeByApplication(scopes, Scope.UpdateUser, Application.EnrollTrack);

  return (
    <Paper className="enroll-track-user-type-entry">
      <IconButton
        className="enroll-track-user-type-entry__remove-button"
        onClick={() => dispatch(removeUserType(enrollTrackUserType))}
        disabled={!hasUpdateAccess && props.isEdit}
      >
        <Close />
      </IconButton>
      <p className="enroll-track-user-type-entry__title">Add EnrollTrack Permissions</p>
      <p className="enroll-track-user-type-entry__description">
        Select a role and complete the appropriate fields to add EnrollTrack permissions to this user.
      </p>
      <OutlinedSelect
        className="enroll-track-user-type-entry__role-select"
        label="EnrollTrack Role"
        value={enrollTrackRole || ''}
        onChange={e => {
          const selectedEnrollTrackRole = parseInt(e.target.value, 10) as EnrollTrackRole;
          if (
            !hasEnrollTrackAgentConnection &&
            (selectedEnrollTrackRole === EnrollTrackRole.Agent || selectedEnrollTrackRole === EnrollTrackRole.Broker)
          )
            dispatch(
              createSnackbar(
                'EnrollTrack Agent or Broker will not see their book of business until they have been assigned role in Agent line of business.'
              )
            );
          if (!rolesThatCanSupportUsers.includes(selectedEnrollTrackRole)) {
            dispatch(changeIsUserSupport(false));
          }
          dispatch(changeEnrollTrackRole(selectedEnrollTrackRole));
        }}
        disabled={!hasUpdateAccess && props.isEdit}
      >
        {!isExternal && <MenuItem value={EnrollTrackRole.Admin}>Admin</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.Agent}>Agent</MenuItem>}
        {isExternal && <MenuItem value={EnrollTrackRole.Broker}>Broker</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.Director}>Director</MenuItem>}
        {isExternal && <MenuItem value={EnrollTrackRole.Employer}>Employer</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.EtsDataOperations}>ETS Data Operations</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.EtsEnrollmentOperations}>ETS Enrollment Operations</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.EtsLeadership}>ETS Leadership</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.HomeOffice}>Home Office</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.SalesDistribution}>Sales and Distribution</MenuItem>}
        {!isExternal && <MenuItem value={EnrollTrackRole.SalesSupport}>Sales Support</MenuItem>}
        {isExternal && (
          <MenuItem value={EnrollTrackRole.EnrollmentTechnologyPartner}>Enrollment Technology Partner</MenuItem>
        )}
      </OutlinedSelect>

      {rolesThatCanSupportUsers.includes(enrollTrackRole) && (
        <FormControlLabel
          control={
            <Switch
              checked={isUserSupport}
              onChange={e => dispatch(changeIsUserSupport(e.target.checked))}
              color="primary"
            />
          }
          label="Can Emulate Users?"
        />
      )}
    </Paper>
  );
};

export default EnrollTrackUserTypeEntry;
